var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from 'react';
import { Stack, Typography } from '@mui/material';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import CustomButton from '../../../components/buttonComponent/CustomButton';
import { baseColors } from '../../../Themes/colors';
import { isFundingLogRowLocked, isFundingLogRowReturnedToCM } from './useFundingLog';
var BOOKED_BANNER_BG = '#DFF3E1';
var BOOKED_BANNER_COLOR = '#2E7D32';
var SALES_SUPPORT_BANNER_BG = baseColors.greyPreset[50];
var SALES_SUPPORT_BANNER_COLOR = baseColors.greyPreset[10];
var LEFT_ALIGN_SX = { justifyContent: 'flex-start' };
var ROW_BUTTON_SX = __assign(__assign({}, LEFT_ALIGN_SX), { height: 'auto', minHeight: 36, minWidth: 0, px: 1.25, py: 0.5, lineHeight: 1.2, textAlign: 'left', '& .MuiButton-startIcon': { mr: 0.75 } });
var FundingLogActionsCard = function (_a) {
    var status = _a.status, tier = _a.tier, assigneeName = _a.assigneeName, currentUserRoleTier = _a.currentUserRoleTier, _b = _a.canManage, canManage = _b === void 0 ? true : _b, onAssignToMyself = _a.onAssignToMyself, onMoveToL2Manager = _a.onMoveToL2Manager, onMoveToSalesSupport = _a.onMoveToSalesSupport, onSetToFunded = _a.onSetToFunded, onReassignToOther = _a.onReassignToOther, onReturnToCM = _a.onReturnToCM, sx = _a.sx;
    var containerSx = __assign({ p: 2 }, sx);
    var isLocked = isFundingLogRowLocked(status);
    var canClaimSecondReviewAsSelf = tier === 'L2' && !assigneeName && currentUserRoleTier === 'L2';
    var canAssignToMyself = !tier || canClaimSecondReviewAsSelf;
    var canReassignToL2 = tier === 'L1' || (!tier && currentUserRoleTier === 'L1');
    var canReassignToSalesSupport = tier === 'L1' || (tier === 'L2' && !canClaimSecondReviewAsSelf);
    var canSetToFunded = Boolean(tier) && !canClaimSecondReviewAsSelf;
    var canReassignToOtherAsL2 = Boolean(tier) && currentUserRoleTier === 'L2' && !canClaimSecondReviewAsSelf && tier !== 'SALES_SUPPORT';
    var canReturnToCM = Boolean(onReturnToCM) && !isFundingLogRowReturnedToCM(status);
    if (isLocked) {
        return (React.createElement(Stack, { sx: containerSx },
            React.createElement(Stack, { direction: "row", alignItems: "center", gap: 1.25, sx: {
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: BOOKED_BANNER_BG,
                } },
                React.createElement(LockOutlinedIcon, { sx: { fontSize: 20, color: BOOKED_BANNER_COLOR, flexShrink: 0 } }),
                React.createElement(Typography, { sx: { fontSize: 13, fontWeight: 600, color: BOOKED_BANNER_COLOR } }, "Deal is set to fund \u2014 booked in Aspire, no further changes."))));
    }
    var actionButtons = [];
    if (canReturnToCM) {
        actionButtons.push({
            key: 'returnToCM',
            render: function (buttonSx) { return (React.createElement(CustomButton, { variant: "outlined", startIcon: React.createElement(NorthEastIcon, { sx: { fontSize: 16 } }), onClick: onReturnToCM, borderColor: baseColors.successPreset[30], textColor: baseColors.successPreset[30], bgColor: baseColors.fullWhite, sx: buttonSx, SMButton: true }, "Return to CM")); },
        });
    }
    if (canManage && canAssignToMyself) {
        actionButtons.push({
            key: 'assignToMyself',
            render: function (buttonSx) { return (React.createElement(CustomButton, { variant: "outlined", startIcon: React.createElement(PersonAddAlt1Icon, { sx: { fontSize: 16 } }), onClick: onAssignToMyself, borderColor: baseColors.successPreset[30], textColor: baseColors.successPreset[30], bgColor: baseColors.fullWhite, sx: buttonSx, SMButton: true }, "Assign to Myself")); },
        });
    }
    if (canManage && canReassignToL2) {
        actionButtons.push({
            key: 'moveToL2',
            render: function (buttonSx) { return (React.createElement(CustomButton, { variant: "outlined", startIcon: React.createElement(NorthEastIcon, { sx: { fontSize: 16 } }), onClick: onMoveToL2Manager, borderColor: baseColors.greyPreset[40], textColor: baseColors.darkPreset[10], bgColor: baseColors.fullWhite, sx: buttonSx, SMButton: true }, tier === 'L1' ? 'Reassign for Final Reviewer' : 'Assign to Final Review')); },
        });
    }
    if (canManage && canReassignToSalesSupport) {
        actionButtons.push({
            key: 'returnToSalesSupport',
            render: function (buttonSx) { return (React.createElement(CustomButton, { variant: "outlined", startIcon: React.createElement(PauseCircleOutlineIcon, { sx: { fontSize: 16 } }), onClick: onMoveToSalesSupport, borderColor: "#F0C48A", textColor: "#B26A00", bgColor: baseColors.fullWhite, sx: buttonSx, SMButton: true }, "Return to Sales Support")); },
        });
    }
    if (canReassignToOtherAsL2) {
        actionButtons.push({
            key: 'reassignToOther',
            render: function (buttonSx) { return (React.createElement(CustomButton, { variant: "outlined", startIcon: React.createElement(PeopleOutlineIcon, { sx: { fontSize: 16 } }), onClick: onReassignToOther, borderColor: baseColors.greyPreset[40], textColor: baseColors.darkPreset[10], bgColor: baseColors.fullWhite, sx: buttonSx, SMButton: true }, "Transfer to Another CM")); },
        });
    }
    var actionsInRow = actionButtons.length === 2;
    return (React.createElement(Stack, { gap: 1.5, sx: containerSx },
        !canManage && tier === 'SALES_SUPPORT' && !canReturnToCM && (React.createElement(Stack, { direction: "row", alignItems: "center", gap: 1.25, sx: {
                p: 1.5,
                borderRadius: 2,
                backgroundColor: SALES_SUPPORT_BANNER_BG,
            } },
            React.createElement(PauseCircleOutlineIcon, { sx: { fontSize: 20, color: SALES_SUPPORT_BANNER_COLOR, flexShrink: 0 } }),
            React.createElement(Typography, { sx: { fontSize: 13, fontWeight: 600, color: SALES_SUPPORT_BANNER_COLOR } }, "Deal is currently returned to Sales Support \u2014 no further action required."))),
        !canManage && tier !== 'SALES_SUPPORT' && !canReturnToCM && !canReassignToOtherAsL2 && (React.createElement(Typography, { sx: { fontSize: 13, color: baseColors.greyPreset[30] } },
            "Assigned to ",
            assigneeName || 'another reviewer',
            " - no actions available to you.")),
        actionButtons.length > 0 && (React.createElement(Stack, { direction: actionsInRow ? 'row' : 'column', gap: 1.5, alignItems: "stretch", sx: actionsInRow ? { '& > *': { flex: '1 1 0', minWidth: 0 } } : undefined }, actionButtons.map(function (_a) {
            var key = _a.key, render = _a.render;
            return (React.createElement(React.Fragment, { key: key }, render(actionsInRow ? ROW_BUTTON_SX : LEFT_ALIGN_SX)));
        })))));
};
export default FundingLogActionsCard;
